/**
 * builder.js — the calculators on the service pages. The visitor configures, the ticket shows what's included
 * plus a guide price, and the request (configuration + prices) goes to Netlify Forms ("aanvraag-<page>").
 * One service per page (hidden input "dienst") or tabs (consultancy page: consultancy | coasters).
 *
 * Every amount comes from RT.prices = src/content/prices.mjs (tarievenkaart v8) — change prices there, not here.
 * Rules encoded here:
 *   - event = setup + guests × cocktails p.p. × rate, + service hours above 3 h and every extra hour
 *   - second bartender from 60 guests, paid for bar hours + 2 h (set-up & teardown)
 *   - Essential only: tasting session (from 40 guests, deducted on booking) and party-colour menu & invitation
 *   - corporate gets one bespoke cocktail included · mileage only above 30 km, return trip · minimum per event
 *   - consultancy: extended audit above 40 items, extra venues on the audit, Plus replaces the basic module
 */
import { $, $$, reduce, gsap, RT, goTo } from "./core.js";

export function initBuilder() {
  const form = $("[data-qb]");
  if (!form) return;
  const Q = RT.calc, T = Q.ticket, F = Q.f;
  const lines = $("#tLines"), status = $("#qbStatus"), submit = $('button[type="submit"]', form);
  const label = $(".qb__label", submit), cfgField = form.elements.configuratie, mailAlt = $("[data-mailalt]", form);
  const panels = $$(".panel", form);

  // ticket number: stable per visit, looks like a real docket
  const d = new Date();
  $("#tNo").textContent = `RB-${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, "0")}-${Math.floor(1000 + Math.random() * 9000)}`;

  const val = (n) => form.elements[n]?.value ?? "";
  const on = (n) => !!form.elements[n]?.checked;
  const checkedLabels = (prefix) => $$(`input[type=checkbox][name^="${prefix}"]`, form).filter((i) => i.checked && !i.disabled).map((i) => i.value);
  const svc = () => (form.querySelector('input[name="dienst"]:checked') || form.elements.dienst).value;
  const PR = RT.prices, TP = T.price, eur = (v) => new Intl.NumberFormat(RT.lang === "nl" ? "nl-BE" : "en-IE",
    { style: "currency", currency: "EUR", minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(v);
  const radioIndex = (n) => $$(`input[name="${n}"]`, form).findIndex((i) => i.checked);
  const checkedIdx = (prefix) => $$(`input[type=checkbox][name^="${prefix}"]`, form).map((i, k) => (i.checked && !i.disabled ? k : -1)).filter((k) => k >= 0);
  const coasterUnit = (q) => PR.coasters.tiers.find(([from]) => q >= from)[1];

  // ---- which panel is active: others are disabled so their fields aren't submitted
  function showPanel() {
    panels.forEach((p) => { const active = p.dataset.panel === svc(); p.classList.toggle("on", active); p.disabled = !active; });
  }

  // ---- Essential-only extras + tasting threshold
  function gate() {
    const pkg = val("pakket"), guests = +val("gasten");
    $$("[data-only]", form).forEach((i) => {
      const ok = pkg === i.dataset.only && (i.name !== "extra_proefmoment" || guests >= 40);
      i.disabled = !ok; if (!ok) i.checked = false;
      i.closest(".chk").classList.toggle("is-off", !ok);
    });
  }

  // ---- build the ticket (array of [label, value, sub?])
  function rows() {
    const r = [[T.service, T.services[svc()]]];
    if (svc() === "events") {
      const pkg = val("pakket"), P = RT.pkgs[pkg], guests = +val("gasten");
      const extraH = +val("extra_uren") || 0, extraS = +val("extra_serves") || 0, corp = val("soort") === F.corporate;
      r.push([T.pkg, pkg], [F.type, val("soort")], [F.guests, guests]);
      r.push([T.bar, `${P.h + extraH} ${T.h}${extraH ? ` (${P.h} + ${extraH})` : ""}`]);
      r.push([T.poured, `± ${guests * P.cpp}`]);
      r.push([T.card, `${P.card + extraS}`], ["", T.free2, true]);
      r.push([T.bartenders, guests >= 60 ? "2" : "1"]);
      r.push([T.tasting, pkg === "Essential" ? (on("extra_proefmoment") ? "4 serves" : "—") : P.tasting]);
      const km = +val("afstand") || 0;
      r.push([T.travel, km <= PR.events.kmFree ? T.travelNear : `${km} km`]);
      if (val("datum")) r.push([F.date, new Date(val("datum")).toLocaleDateString(RT.lang === "nl" ? "nl-BE" : "en-GB")]);
      if (val("locatie")) r.push([F.place, val("locatie")]);
      const ex = checkedLabels("extra_");
      if (ex.length) { r.push([F.extras, ex.length]); ex.forEach((e) => r.push(["", e, true])); }
      if (corp) { r.push([T.corp, "✓"]); RT.corp.forEach((c) => r.push(["", c, true])); }
    } else if (svc() === "consultancy") {
      r.push([F.venue, val("zaak")], [T.items, val("items")], [T.venues, val("vestigingen")]);
      const m = checkedLabels("module_"); r.push([T.modules, m.length || "—"]); m.forEach((x) => r.push(["", x, true]));
    } else if (svc() === "freelance") {
      const hrs = Math.max(4, +val("uren")), n = Math.max(1, +val("shifts"));
      r.push([T.shifts, n], [F.hours, `${hrs} ${T.h}`], [T.totalHours, `${n * hrs} ${T.h}`]);
      r.push([F.rate, val("tarief")]);
      if (val("freelance_datum")) r.push([F.date, val("freelance_datum")]);
    } else if (svc() === "coasters") {
      r.push([T.qty, Math.max(50, +val("coasters_aantal"))], [F.use, val("coasters_voor")], [F.logo, on("coasters_logo") ? T.yes : T.no2], [T.colors, val("coasters_kleuren")]);
    }
    return r;
  }

  // ---- guide price: [label, amount | text, sub?] lines + totals
  function price() {
    const out = { lines: [], total: 0, perGuest: 0, quarter: 0 };
    const add = (label, v, note) => { out.lines.push([label, note ? `${eur(v)} · ${note}` : eur(v)]); out.total += v; };
    const info = (label, text) => out.lines.push([label, text]);
    if (svc() === "events") {
      const E = PR.events, pkg = val("pakket"), P = RT.pkgs[pkg], g = +val("gasten"), seg = val("soort") === F.corporate ? 1 : 0;
      const extraH = +val("extra_uren") || 0, extraS = +val("extra_serves") || 0, km = +val("afstand") || 0;
      add(TP.setup, E.setup[seg]);
      add(`${TP.cocktails} · ${g} × ${P.cpp} × ${eur(E.perCocktail[seg])}`, g * P.cpp * E.perCocktail[seg]);
      if (on("extra_reserve")) add(`${TP.reserve} · + ${eur(E.reserve)}`, g * P.cpp * E.reserve);
      const hrs = Math.max(0, P.h - E.includedHours) + extraH;
      if (hrs) add(`${TP.hours} · ${hrs} × ${eur(E.hour[seg])}`, hrs * E.hour[seg]);
      if (g >= E.bartender2From) { const h2 = P.h + extraH + E.bartender2ExtraHours; add(`${TP.bartender2} · ${h2} ${T.h} × ${eur(E.bartender2[seg])}`, h2 * E.bartender2[seg]); }
      if (extraS) add(`${TP.recipes} · ${extraS} × ${eur(E.extraRecipe)}`, extraS * E.extraRecipe);
      if (on("extra_opmaat")) seg ? info(TP.bespoke, TP.included) : add(TP.bespoke, E.bespoke);
      if (on("extra_design")) add(TP.design, E.design);
      if (on("extra_proefmoment")) info(TP.tasting, `${eur(E.tasting)} · ${TP.settled}`);
      if (on("extra_coasters")) { const q = Math.max(PR.coasters.min, g); add(`${TP.coasters} · ${eur(PR.coasters.setup)} + ${q} × ${eur(coasterUnit(q))}`, PR.coasters.setup + q * coasterUnit(q)); }
      if (km > E.kmFree) { const extra = km - E.kmFree; add(`${TP.km} · ${extra} × 2 × ${eur(E.kmRate)}`, extra * 2 * E.kmRate); }
      if (out.total < E.minimum[seg]) add(TP.minimum, E.minimum[seg] - out.total);
      out.perGuest = out.total / g;
    } else if (svc() === "consultancy") {
      const M = PR.consultancy.modules, names = F.moduleOpts, items = +val("items") || 0, venues = Math.max(1, +val("vestigingen") || 1);
      let picked = checkedIdx("module_");
      // Plus replaces the basic version of the same module
      picked = picked.filter((i) => !M[i].group || !picked.some((j) => j > i && M[j].group === M[i].group));
      picked.forEach((i) => {
        const m = M[i];
        if (m.quarter) { out.quarter += m.quarter; out.lines.push([`${names[i]}`, `${eur(m.quarter)} · ${TP.quarter.toLowerCase()}`]); return; }
        if (m.key === "audit") {
          const xl = items >= m.xlFromItems;
          add(xl ? `${names[i]} · ${TP.xl}` : names[i], xl ? m.xl : m.price);
          if (venues > 1) add(`${TP.venue} · ${venues - 1} × ${eur(m.perVenue)}`, (venues - 1) * m.perVenue);
          return;
        }
        if (!m.price) { info(names[i], TP.free); return; }
        m.print ? add(names[i], m.price, TP.print) : add(names[i], m.price);
      });
    } else if (svc() === "coasters") {
      const C = PR.coasters, q = Math.max(C.min, +val("coasters_aantal") || 0);
      add(TP.coasterSetup, C.setup);
      add(`${q} × ${eur(coasterUnit(q))} ${TP.perPiece}`, q * coasterUnit(q));
      out.note = TP.setupFree;
    } else if (svc() === "freelance") {
      const Fr = PR.freelance, h = Math.max(Fr.minHours, +val("uren") || 0), n = Math.max(1, +val("shifts") || 1);
      const rate = radioIndex("tarief") === 1 ? Fr.rateHigh : Fr.rate;
      add(`${n} × ${h} ${T.h} × ${eur(rate)}`, n * h * rate);
    }
    return out;
  }

  let prev = {};
  function render() {
    const r = rows(), pr = price();
    const totals = [];
    if (pr.total || !pr.quarter) totals.push([svc() === "consultancy" && pr.quarter ? TP.once : TP.total, pr.total ? eur(pr.total) : TP.free]);
    if (pr.quarter) totals.push([TP.quarter, `${eur(pr.quarter)} · ${eur(pr.quarter * 4)} ${TP.year}`]);
    if (pr.perGuest) totals.push([TP.perGuest, eur(Math.round(pr.perGuest * 100) / 100)]);
    const all = [...r, [TP.title, "", "head"], ...pr.lines.map(([k, v]) => [k, v, "price"]), ...totals.map(([k, v], i) => [k, v, i ? "per" : "total"])];
    if (pr.note) all.push(["", pr.note, "sub"]);
    lines.innerHTML = all.map(([k, v, kind], i) => `<div class="${kind === true ? "sub" : kind || ""}"><dt>${k}</dt><dd data-i="${i}">${v}</dd></div>`).join("");
    // flash values that changed since the last render (the "docket updates" feel)
    const now = {};
    $$("dd", lines).forEach((dd) => { const key = dd.previousElementSibling.textContent + dd.dataset.i; now[key] = dd.textContent; if (prev[key] !== undefined && prev[key] !== dd.textContent) dd.classList.add("flash"); });
    prev = now;
    const text = all.map(([k, v, kind]) => kind === "head" ? `\n${k}` : `${k ? k + ": " : "  · "}${v}`).join("\n");
    cfgField.value = text;
    const subject = encodeURIComponent(`${T.title} — ${T.services[svc()]}`);
    mailAlt.href = `mailto:${RT.mail}?subject=${subject}&body=${encodeURIComponent(text + "\n\n")}`;
  }

  // ---- steppers, range output, service links elsewhere on the page
  form.addEventListener("click", (e) => {
    const b = e.target.closest("[data-step]"); if (!b) return;
    const inp = b.parentElement.querySelector("input");
    const v = (+inp.value || 0) + +b.dataset.step;
    inp.value = Math.min(+inp.max || Infinity, Math.max(+inp.min || 0, v));
    inp.dispatchEvent(new Event("input", { bubbles: true }));
  });
  const guests = form.elements.gasten, guestsOut = $("#q-gasten-out");
  const sync = () => {
    if (guests && guestsOut) guestsOut.textContent = guests.value;
    // sliders: fill the track up to the thumb (read by the CSS as --fill)
    $$('input[type="range"]', form).forEach((r) => r.style.setProperty("--fill", `${((r.value - r.min) / (r.max - r.min)) * 100}%`));
  };
  // "Bereken" on a package card → select that package here
  document.addEventListener("click", (e) => {
    const a = e.target.closest("[data-pkg]"); if (!a) return;
    const r = form.querySelector(`input[name="pakket"][value="${a.dataset.pkg}"]`);
    if (r) { r.checked = true; update(); }
  });
  // "Bereken" in a consultancy service → consultancy tab, exactly those modules ticked (data-modules = 0-based indexes)
  document.addEventListener("click", (e) => {
    const a = e.target.closest("[data-modules]"); if (!a) return;
    const tab = form.querySelector('input[name="dienst"][value="consultancy"]'); if (!tab) return;
    tab.checked = true;
    const want = a.dataset.modules.split(",").map(Number);
    $$('input[type=checkbox][name^="module_"]', form).forEach((c, i) => (c.checked = want.includes(i)));
    update();
  });
  // links like /consultancy/#calculator?coasters or #coasters → open the matching tab
  document.addEventListener("click", (e) => {
    const a = e.target.closest("[data-svc]"); if (!a) return;
    const r = form.querySelector(`input[name="dienst"][value="${a.dataset.svc}"]`);
    if (r) { r.checked = true; update(); }
  });

  function update() { showPanel(); gate(); sync(); render(); }
  form.addEventListener("input", update);
  form.addEventListener("change", update);
  update();

  // ---- validation (only name + email are required)
  const errs = { naam: $("#q-naam-err"), email: $("#q-email-err") };
  function validate() {
    let first = null;
    for (const n of ["naam", "email"]) {
      const el = form.elements[n], v = el.value.trim();
      let msg = v ? "" : Q.form.required;
      if (!msg && n === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) msg = Q.form.invalidEmail;
      el.setAttribute("aria-invalid", String(!!msg));
      if (msg) el.setAttribute("aria-describedby", errs[n].id); else el.removeAttribute("aria-describedby");
      errs[n].textContent = msg;
      if (msg && !first) first = el;
    }
    if (first) { goTo(first, false); first.focus({ preventScroll: true }); if (!reduce) gsap.fromTo(first, { x: -6 }, { x: 0, duration: 0.4, ease: "elastic.out(1,0.3)" }); }
    return !first;
  }
  ["naam", "email"].forEach((n) => form.elements[n].addEventListener("blur", () => form.elements[n].getAttribute("aria-invalid") === "true" && validate()));

  // ---- submit to Netlify Forms (progressive: without JS the form posts to the thank-you page)
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    render();
    if (!validate()) return;
    submit.setAttribute("aria-busy", "true"); label.textContent = Q.form.sending;
    status.className = "qb__status"; status.textContent = "";
    try {
      const body = new URLSearchParams(new FormData(form)).toString();
      const res = await fetch("/", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body });
      if (!res.ok) throw new Error(res.status);
      form.classList.add("is-sent");
      status.className = "qb__status ok"; status.textContent = Q.form.ok;
      label.textContent = Q.form.okStamp; submit.disabled = true;
    } catch (err) {
      status.className = "qb__status bad";
      status.innerHTML = `${Q.form.err} <a href="${mailAlt.href}">${RT.mail}</a>`;
      label.textContent = Q.form.submit;
    } finally { submit.removeAttribute("aria-busy"); }
  });
}
